import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

const STORAGE_KEY = 'rm_consent';
const CONSENT_VERSION = '1.0';

export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveConsent = (prefs) => {
  const payload = {
    ...prefs,
    necessary: true,
    version: CONSENT_VERSION,
    saved_at: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: payload }));
  return payload;
};

export const CookieConsentBanner = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false, functional: true });

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing || existing.version !== CONSENT_VERSION) {
      // Slight delay so the banner doesn't flash before page paint
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  // Allow re-opening from any "Manage cookies" link
  useEffect(() => {
    const handler = () => {
      const existing = getCookieConsent();
      if (existing) setPrefs({ analytics: !!existing.analytics, marketing: !!existing.marketing, functional: existing.functional !== false });
      setVisible(true);
      setShowSettings(true);
    };
    window.addEventListener('open-cookie-settings', handler);
    return () => window.removeEventListener('open-cookie-settings', handler);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true, functional: true });
    setVisible(false);
  };
  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false, functional: false });
    setVisible(false);
  };
  const saveCustom = () => {
    saveConsent(prefs);
    setVisible(false);
  };

  const T = (fr, en) => (language === 'fr' ? fr : en);

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:pb-6 pointer-events-none"
    >
      <div className="max-w-4xl mx-auto pointer-events-auto">
        <div className="bg-white border border-[#0F4C5C]/15 rounded-2xl shadow-2xl shadow-[#0F4C5C]/15 overflow-hidden">
          <div className="p-5 sm:p-6 md:p-7">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex w-12 h-12 rounded-xl bg-[#E9C46A]/15 border border-[#E9C46A]/30 items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-[#E9C46A]" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-[#0F4C5C] tracking-tight mb-1">
                  {T('Votre vie privée, votre choix', 'Your privacy, your choice')}
                </h3>
                <p className="text-sm text-[#0F4C5C]/75 leading-relaxed">
                  {T(
                    'Nous utilisons des cookies pour faire fonctionner le site, mesurer son audience et améliorer votre expérience. Les cookies non essentiels ne sont déposés qu\'avec votre accord.',
                    'We use cookies to make this site work, measure traffic and improve your experience. Non-essential cookies are only stored with your consent.'
                  )}{' '}
                  <Link to="/cookies" className="underline decoration-[#E9C46A] underline-offset-2 hover:text-[#E9C46A]">
                    {T('En savoir plus', 'Learn more')}
                  </Link>
                </p>

                {showSettings && (
                  <div className="mt-5 space-y-3 border-t border-[#0F4C5C]/10 pt-5">
                    <ConsentRow
                      title={T('Strictement nécessaires', 'Strictly necessary')}
                      desc={T('Authentification, panier, sécurité. Toujours actifs.', 'Auth, cart, security. Always on.')}
                      checked
                      disabled
                    />
                    <ConsentRow
                      title={T('Fonctionnels', 'Functional')}
                      desc={T('Mémorisation de la langue, des préférences.', 'Remember language and preferences.')}
                      checked={prefs.functional}
                      onChange={(v) => setPrefs({ ...prefs, functional: v })}
                    />
                    <ConsentRow
                      title={T('Mesure d\'audience', 'Analytics')}
                      desc={T('Statistiques anonymes pour améliorer le site.', 'Anonymous statistics to improve the site.')}
                      checked={prefs.analytics}
                      onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                    />
                    <ConsentRow
                      title={T('Marketing', 'Marketing')}
                      desc={T('Publicités personnalisées sur d\'autres sites.', 'Personalised ads on other websites.')}
                      checked={prefs.marketing}
                      onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                    />
                  </div>
                )}

                <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-2.5">
                  {showSettings ? (
                    <>
                      <Button onClick={saveCustom} className="btn-primary flex-1 sm:flex-none" data-testid="cookie-save-custom-btn">
                        {T('Enregistrer mes choix', 'Save my choices')}
                      </Button>
                      <Button onClick={acceptAll} variant="outline" className="flex-1 sm:flex-none" data-testid="cookie-accept-all-btn">
                        {T('Tout accepter', 'Accept all')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={acceptAll} className="btn-primary flex-1 sm:flex-none" data-testid="cookie-accept-all-btn">
                        {T('Tout accepter', 'Accept all')}
                      </Button>
                      <Button onClick={rejectAll} variant="outline" className="flex-1 sm:flex-none" data-testid="cookie-reject-all-btn">
                        {T('Tout refuser', 'Reject all')}
                      </Button>
                      <Button
                        onClick={() => setShowSettings(true)}
                        variant="ghost"
                        className="flex-1 sm:flex-none text-[#0F4C5C]"
                        data-testid="cookie-customize-btn"
                      >
                        <Settings className="w-4 h-4 mr-1.5" />
                        {T('Personnaliser', 'Customise')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={rejectAll}
                aria-label="Close"
                className="text-[#0F4C5C]/40 hover:text-[#0F4C5C] transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConsentRow = ({ title, desc, checked, onChange, disabled }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1">
      <p className="font-semibold text-sm text-[#0F4C5C]">{title}</p>
      <p className="text-xs text-[#0F4C5C]/65 mt-0.5">{desc}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
  </div>
);
